import gql from "graphql-tag";

export const Account = gql`
  query Account {
    account {
      accountId
      name
      email
      integrations {
        id
      }
      brands {
        brandId
        logoImageUrl
        primaryColor
        secondaryColor
      }
      onboardingComplete
      subscribers {
        customerCount
        subscriberCount
        dateRefreshed
      }
    }
  }
`;

export const Campaigns = gql`
  query Campaigns($input: CampaignsInput) {
    campaignsPaginated(input: $input) {
      campaigns {
        campaignId
        accountId
        status
        subject
        status
        dateCreated
        previewUrl
        stats {
          dateSent
          sentCount
          openCount
          clickCount
        }
      }
      paginationKey
    }
  }
`;

export const Brand = gql`
  query Brand {
    brand {
      logoImageUrl
      primaryColor
      secondaryColor
    }
  }
`;

export const Campaign = gql`
  query Campaign($input: CampaignInput) {
    campaign(input: $input) {
      campaignId
      accountId
      template {
        sections {
          sectionId
          order
          name
          widgetId
          locked
        }
      }
      status
      subject
      dateCreated
      stats {
        dateSent
        sentCount
        openCount
        clickCount
      }
      fields {
        id
        label
        valueField
        ... on BooleanSetting {
          booleanValue
        }
        ... on ColorSetting {
          colorValue
        }
        ... on FileSetting {
          fileValue
        }
        ... on NumberSetting {
          numberValue
          min
          max
        }
        ... on TextSetting {
          textValue
          multiline
          maxLength
          default
        }
        ... on SelectorSetting {
          selectorValue
          items {
            label
            value
          }
        }
        ... on ElementSetting {
          elementValue
          selectedElement {
            category {
              label
              description
            }
            elementId
            asset {
              ... on ImageAssetType {
                image {
                  alt
                  src
                }
              }
            }
            previewImage {
              alt
              src
            }
            price
            tags {
              value
            }
            name
            type {
              elementTypeId
              label
            }
          }
          id
          label
          message
          valueField
          elementTypes {
            elementTypeId
            label
          }
        }
      }
    }
  }
`;

export const Section = gql`
  query Section($input: SectionInput) {
    section(input: $input) {
      sectionId
      order
      name
      widgetId
      locked
      settings {
        id
        __typename
        label
        message
        ... on FileSetting {
          fileValue
          valueField
        }
        ... on SelectorSetting {
          selectorValue
          valueField
          items {
            label
            value
          }
        }
        ... on BooleanSetting {
          booleanValue
          valueField
        }
        ... on ColorSetting {
          colorValue
          valueField
        }
        ... on TextSetting {
          textValue
          valueField
          multiline
          maxLength
          default
        }
        ... on NumberSetting {
          numberValue
          valueField
          min
          max
        }
        ... on ElementSetting {
          elementValue
          valueField
          id
          selectedElement {
            category {
              label
              description
            }
            elementId
            asset {
              ... on ImageAssetType {
                image {
                  alt
                  src
                }
              }
            }
            previewImage {
              alt
              src
            }
            price
            tags {
              value
            }
            name
            type {
              elementTypeId
              label
            }
          }
          label
          message
          valueField
          elementTypes {
            elementTypeId
            label
          }
        }
      }
    }
  }
`;

export const Widgets = gql`
  query Widgets {
    widgets {
      id
      name
      description
      integrationType
      category
      imageUrl
      iconUrl
    }
  }
`;

export const Preview = gql`
  query Preview($input: PreviewInput) {
    preview(input: $input) {
      previewUrl
    }
  }
`;

export const Industries = gql`
  query Industries {
    industries {
      id
      label
      tags {
        value
      }
    }
  }
`;

export const SendCampaignCheck = gql`
  query sendCampaignCheck($input: SendCampaignCheckInput) {
    sendCampaignCheck(input: $input) {
      campaignCheck {
        nextSendAt
        sendCampaignFailed
        sendCampaignFailedReason
      }
      details {
        campaignsSentThisMonth
        customerCount
        emailsSentThisMonth
        lastSendAt
        subscriberCount
      }
      packageCheck {
        currentPackage {
          id
        }
        requiredPackage {
          name
          plans {
            price
            discountPercentage
            subscription
            upgradeUrl
          }
          features {
            maxEmails
            maxCampaigns
            maxSubscribers
            removePromoBranding
          }
        }
        upgradeRequired
        upgradeRequiredReasons
      }
    }
  }
`;

export const Packages = gql`
  query packages {
    packages {
      id
      name
      plans {
        price
        discountPercentage
        current
        subscription
        upgradeUrl
      }
      features {
        maxCampaigns
        maxEmails
        maxSubscribers
        removePromoBranding
        customElements
      }
    }
  }
`;

export const Elements = gql`
  query Elements($input: ElementsInput) {
    elements(input: $input) {
      elements {
        elementId
        status
        name
        previewImage {
          src
        }
        asset {
          ... on ImageAssetType {
            image {
              src
            }
          }
        }
        price
        type {
          elementTypeId
          label
        }
        category {
          categoryId
          label
          description
        }
        tags {
          value
        }
      }
      paginationKey
    }
  }
`;

export const ElementTypes = gql`
  query ElementTypes {
    elementTypes {
      label
      elementTypeId
    }
  }
`;

export const Categories = gql`
  query Categories {
    categories {
      categoryId
      label
      description
    }
  }
`;

export const BaseTemplates = gql`
  query baseTemplates($input: BaseTemplatesInput!) {
    baseTemplates(input: $input) {
      baseTemplates {
        baseTemplateId
        name
        description
        previewImage {
          src
          alt
        }
        category {
          categoryId
          description
          label
        }
        type {
          label
          description
        }
        price
      }
      paginationKey
    }
  }
`;

export const BaseTemplate = gql`
  query baseTemplate($input: BaseTemplateInput!) {
    baseTemplate(input: $input) {
      baseTemplateId
      name
      description
      previewImage {
        src
        alt
      }
      category {
        categoryId
        description
        label
      }
      type {
        label
        description
      }
      price
    }
  }
`;

export const TemplateTypes = gql`
  query TemplateTypes {
    templateTypes {
      templateTypeId
      label
      description
    }
  }
`;

export const FeatureCheck = gql`
  query FeatureCheck($input: FeatureCheckInput!) {
    featureCheck(input: $input) {
      upgradeRequired
      requiredPackage {
        name
        plans {
          price
          discountPercentage
          subscription
          upgradeUrl
        }
      }
    }
  }
`;
